import React, { PropsWithChildren } from 'react';
import styled from '@emotion/styled';
import Grid from '@mui/material/Grid';
import { gql } from '@yi/core';
import { i18n_poses } from '@yi/i18n/phrases/i18n_poses';
import { i18n_yoga } from '@yi/i18n/phrases/i18n_yoga';

import { utilities } from '~/common';
import {
  AccessTime,
  ArticlesIcon,
  AuthorNameLight as BaseAuthorNameLight,
  DurationIconButton,
  DurationLight,
  LocalizedLink,
  useLocale,
} from '~/components';
import { breakpoints } from '~/src/styles';

import {
  Author as BaseAuthor,
  Avatar,
  BannerWrapper,
  ClassStyleAndLevel,
  DurationIconButtonWrapper,
  Heading,
  InfoSection as BaseInfoSection,
} from './elements';

type Article = gql.typesSsr.Query_Article_yi_Article;
type Content = Pick<Article, 'title'> &
  Pick<Partial<Article>, 'teacher' | 'secondary_teacher' | 'channel' | 'topic'>;

interface Props {
  content: Content;
  style?: React.CSSProperties;
  isPoseContent?: boolean;
}

const InfoSection = styled(BaseInfoSection)`
  ${breakpoints.minDesktopLg} {
    width: 56%;
    max-width: 740px;
  }
`;

const TeacherAmpersand = styled.p`
  padding-left: 7px;
  padding-right: 8px;
  font-size: 15px;
  color: white;
  display: inline-block;
  vertical-align: top;
`;

const AuthorNameLight = styled(BaseAuthorNameLight)`
  vertical-align: super;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Author = styled(BaseAuthor)`
  display: flex;
  align-items: center;
`;

export const ArticleHeading: React.FC<PropsWithChildren<Props>> = ({
  isPoseContent,
  style,
  content: { channel, topic, title, teacher, secondary_teacher },
}) => {
  const { locale } = useLocale();

  const { encodeQueryString } = utilities;
  let topicLink = null;
  if (isPoseContent) {
    // @TODO do not use i18n to derive URL
    topicLink = (
      <LocalizedLink to={`/articles?topic=${i18n_poses(locale)}`}>
        {i18n_poses(locale)}
      </LocalizedLink>
    );
  } else if (topic) {
    topicLink = (
      <LocalizedLink to={`articles/?topic=${encodeQueryString(topic.name)}`}>
        {topic.name}
      </LocalizedLink>
    );
  }

  let channelLink = null;
  if (isPoseContent) {
    // @TODO do not use i18n to derive URL
    channelLink = (
      <LocalizedLink to={`articles/?query=${i18n_yoga(locale)}`}>{i18n_yoga(locale)}</LocalizedLink>
    );
  } else if (channel) {
    channelLink = (
      <LocalizedLink to={`articles/?query=${channel.name}`}>{channel.name}</LocalizedLink>
    );
  }
  const mediaDuration = { group: '5 min.' };
  return (
    <BannerWrapper style={style}>
      <InfoSection>
        <Grid container>
          <Grid item xs={6}>
            <ClassStyleAndLevel>
              <ArticlesIcon style={{ height: '20px', width: '25px', color: '#fff' }} />
              {channelLink}
              {channel && ' | '}
              {topicLink}
            </ClassStyleAndLevel>
          </Grid>
        </Grid>
        <Heading>{title}</Heading>
        <Grid container>
          <Grid item xs={secondary_teacher ? 10 : 8} sm={secondary_teacher ? 7 : 6}>
            <Author>
              <Avatar imgProps={{ role: 'presentation' }} src={teacher?.image?.processed_url} />
              <AuthorNameLight>
                {teacher && (
                  <LocalizedLink to={`/profile/${teacher.url_title}`}>
                    {teacher.full_name}
                  </LocalizedLink>
                )}
              </AuthorNameLight>
              {secondary_teacher && (
                <>
                  <TeacherAmpersand>&</TeacherAmpersand>

                  <Avatar
                    imgProps={{ role: 'presentation' }}
                    src={secondary_teacher?.image?.processed_url}
                  />
                  <AuthorNameLight>
                    <LocalizedLink to={`profile/${secondary_teacher.url_title}`}>
                      {secondary_teacher.full_name}
                    </LocalizedLink>
                  </AuthorNameLight>
                </>
              )}
            </Author>
          </Grid>
          <Grid item xs={secondary_teacher ? 2 : 4} sm={secondary_teacher ? 5 : 6}>
            {mediaDuration && (
              <DurationIconButtonWrapper>
                <DurationIconButton size="medium" aria-label="Settings">
                  <AccessTime />
                  <DurationLight>{mediaDuration.group}</DurationLight>
                </DurationIconButton>
              </DurationIconButtonWrapper>
            )}
          </Grid>
        </Grid>
      </InfoSection>
    </BannerWrapper>
  );
};
